import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BASE_IMAGE_URL, BASE_URL } from "../../../config/apiConfig";
import { ToolProvider, useToolContext } from "../../../contexts/ToolContext";
import { getHeaders } from "../../../utils/apiUtils";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel
} from "@mui/material";
import { useTheme } from "@emotion/react";
import SelectableList from "../../../components/features/GarmentAttribute/SelectableList";
import ImageWindow from "../../../components/layout/Interactive/ImageWindow";
import KeyEventHandler from "../../../contexts/KeyEventHandler";
import MassImageApprovalsFrame from "../../../components/layout/Interactive/MassImageApprovalsFrame";
import { jobQaMap, jobTypes } from "../../../config/jobTypes";
import { showAlert, showAlertWithMessage } from "../../../components/common/userFeedback/CustomAlert";
import Slider from "@mui/material/Slider";
import TakeActionScreenCover from "../../../components/common/userFeedback/TakeActionScreenCover";
import LoadingOverlay from "../../../components/common/userFeedback/LoadingOverlay";
import NoDataScreenCover from "../../../components/common/userFeedback/NoDataScreenCover";
import { useAmpltiudeEventTrackingContext } from "../../../contexts/AmplitudeTrackingContext";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import { formatToSpacedCapitalizedWords } from "../../../utils/sharedFunctions";
import PipelineJobMultiSelect from "../../../components/forms/Dropdowns/PipelineJobMultiSelect";
import SearchableMultiSelectV2 from "../../../components/forms/Dropdowns/SearchableMultiSelectV2";
import StyledButton from "../../../components/ui/buttons/StyledButton";

function GarmentAttributeQaTool() {
  const {
    jobDetails,
    jobDataLoaded,
    toolType,
    allPipelineJobs,
    shownAttributes,
  } = useToolContext();
  const { palette } = useTheme();
  const [numUsers, setNumUsers] = useState(2);
  const [tagSet, setTagSet] = useState([]);
  const [garments, setGarments] = useState([]);
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [selectAllGarments, setSelectAllGarments] = useState(false);
  const [orderByConfidenceAsc, setOrderByConfidenceAsc] = useState(false);
  const [numberOfColumns, setNumberOfColumns] = useState(6); // Default to 3 columns
  const [garmentLimit, setGarmentLimit] = useState(numberOfColumns * 3);
  
  // Modal states
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  
  // New sorting options
  const [sortBy, setSortBy] = useState("confidence"); // "confidence" or "submitted_timestamp"
  const [orderBySubmittedTimestampAsc, setOrderBySubmittedTimestampAsc] = useState(false);

  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [additionalPossiblePipelineJobs, setAdditionalPossiblePipelineJobs] =
    useState([]);
  const [additionalPipelineJobs, setAdditionalPipelineJobs] = useState([]);
  
  // Add state for proposed_by filter
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const { trackEvent } = useAmpltiudeEventTrackingContext();
  const [qaCountObjects, setQaCountObjects] = useState({});
  const [toolID, setToolID] = useState(0);
  const toolName = toolType ? `${toolType.replace("_QA","").charAt(0).toUpperCase() + toolType.replace("_QA","").slice(1).toLowerCase()}TaggingQATool` : "TaggingQATool"

  useEffect(() => {
    fetchToolData();
  }, [toolName, toolType])
  
  const fetchToolData = async () => {
    const headers = await getHeaders();
    const formattedToolName = formatToSpacedCapitalizedWords(toolName);
    let url = `${BASE_URL}tools/jobs/tool-names?toolName=${toolName}`;
    axios
      .get(url, headers)
      .then((res) => {
        if (res.data.data && res.data.data.length > 0 && res.data.data[0].id && res.data.data[0].id > 0)
        {
          setToolID(res.data.data[0].id)
        } else {
          const err = `Could not find tool ID in 'tool_names' with name '${formattedToolName}' (formatted from '${toolName}').`
          console.log(err);
          showAlert(err);
        }
      })

      .catch((err) => {
        console.log(err);
        showAlert(err);
      });
  };

  //Axios Calls
  const fetchTagSet = async () => {
    const headers = await getHeaders();
    axios
      .get(
        `${BASE_URL}tools/jobs/tag-set/${jobDetails.pipeline_job_link_id}`,
        headers
      )
      .then((res) => {
        setTagSet(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchPipelineJobIds = async () => {
    const headers = await getHeaders();
    const jobsToFetch = jobQaMap[toolType];
    axios
      .get(
        `${BASE_URL}tools/pipelines/list-pipeline-jobs/${jobsToFetch}`,
        headers
      )
      .then((res) => {
        setAdditionalPossiblePipelineJobs(res.data.data);
        console.log(res.data.data);
      })
      .catch((err) => {
        showAlert(err);
      });
  };

  const fetchNextItemBatch = async () => {
    const headers = await getHeaders();
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);
    const fullListofIdsToQA = [...additionalPipelineJobs, ...jobIdsToQa];
    const submissionData = {
      graduationConfidence: jobDetails.qa_confidence,
      toolType: toolType,
      pipelineJobIdsToQa: fullListofIdsToQA,
      selectedTag: selectedTag,
      userCount: numUsers,
      jobID: jobDetails.job_id,
      shownAttributes: shownAttributes,
      orderByConfidenceAsc: orderByConfidenceAsc,
      garmentLimit: garmentLimit,
      distinct: true,
      sortBy: sortBy,
      orderBySubmittedTimestampAsc: orderBySubmittedTimestampAsc,
      proposedByUserIds: selectedUsers && selectedUsers.length > 0 ? selectedUsers.map((u) => u.id) : undefined
    };
    console.log(`SUBDATA: ${JSON.stringify(submissionData)}`)
    setLoading(true);
    await axios
      .post(
        `${BASE_URL}tools/summon/next-item/qa/multi-and`,
        submissionData,
        headers
      )
      .then((res) => {
        console.log(`res.data.data LEN: ${res.data.data.length} ${JSON.stringify(res.data.data.map(x => x.garment_id))}`)
        setGarments(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setGarments([]);
        setLoading(false);
        showAlert(err);
      });
  };

  const fetchQaCount = async () => {
    console.log("Fetching Qa Count");
    const headers = await getHeaders();
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);
    const fullListofIdsToQA = [...additionalPipelineJobs, ...jobIdsToQa];

    const submissionData = {
      graduationConfidence: jobDetails.qa_confidence,
      toolType: toolType,
      pipelineJobIdsToQa: fullListofIdsToQA,
      userCount: numUsers,
      jobID: jobDetails.job_id,
      shownAttributes: shownAttributes,
      tags: tagSet,
      proposedByUserIds: selectedUsers && selectedUsers.length > 0 ? selectedUsers.map((u) => u.id) : undefined,
    };
    setLoading(true);
    await axios
      .post(
        `${BASE_URL}tools/jobs/qa/count`,
        submissionData,
        headers
      )
      .then((res) => {
        setQaCountObjects(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setQaCountObjects({});
        setLoading(false);
        showAlert(err);
      });
  };

  const submitItems = async () => {
    const headers = await getHeaders();
    setLoading(true);
    const rejectedIds = selectedGarments.map((t) => t.garment_id);
    let approved = garments
      .filter((image) => !rejectedIds.includes(image.garment_id))
      .map((image) => image.garment_id);
    let rejected = garments
      .filter((image) => rejectedIds.includes(image.garment_id))
      .map((image) => image.garment_id);

    // Batch size to avoid API Gateway payload limits (10MB default)
    const BATCH_SIZE = 50;
    
    try {
      // Process approved garments in batches
      for (let i = 0; i < approved.length; i += BATCH_SIZE) {
        const approvedBatch = approved.slice(i, i + BATCH_SIZE);
        const data = {
          approved: approvedBatch,
          rejected: [], // Process rejected separately
          proposal_value_id: selectedTag,
          jobId: jobDetails.job_id,
          pipelineJobId: jobDetails.pipeline_job_link_id,
          toolType: toolType,
        };
        
        await axios.post(`${BASE_URL}tools/classify/qa/multi-and`, data, headers);
      }

      // Process rejected garments in batches
      for (let i = 0; i < rejected.length; i += BATCH_SIZE) {
        const rejectedBatch = rejected.slice(i, i + BATCH_SIZE);
        const data = {
          approved: [], // Process approved separately
          rejected: rejectedBatch,
          proposal_value_id: selectedTag,
          jobId: jobDetails.job_id,
          pipelineJobId: jobDetails.pipeline_job_link_id,
          toolType: toolType,
        };
        
        await axios.post(`${BASE_URL}tools/classify/qa/multi-and`, data, headers);
      }

      setLoading(true);
      await fetchNextItemBatch();
      setLoading(true);
      await fetchQaCount();
      setLoading(true);
      setSelectedGarments([]);
      setSelectAllGarments(false);
      setLoading(true);

      trackEvent("ProposalQA", {
        approvedGarmentIds: approved,
        rejectedGarmentIds: rejected,
        pipelineJobId: jobDetails.pipeline_job_link_id,
        pipelineName: jobDetails.pipeline_name,
        jobId: jobDetails.job_id,
        jobName: jobDetails.job,
        toolType: toolType,
        toolName: "GarmentAttributeQATool",
        toolId: toolID,
        confidence: jobDetails.qa_confidence,
        skipped: approved.length === 0,
        qa: true,
      });
    } catch (err) {
      console.log(err);
      showAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setGarmentLimit(numberOfColumns * 3);
  }, [numberOfColumns]);

  //Use Effects
  useEffect(() => {
    if (jobDataLoaded) {
      fetchTagSet();
      fetchPipelineJobIds();
    }
  }, [jobDataLoaded]);

  useEffect(() => {
    if (selectedTag) {
      fetchNextItemBatch();
    }
  }, [selectedTag, numUsers, additionalPipelineJobs, orderByConfidenceAsc, garmentLimit, numberOfColumns, sortBy, orderBySubmittedTimestampAsc, selectedUsers]);

  useEffect(() => {
    if (jobDataLoaded && tagSet && tagSet.length > 0) {
      fetchQaCount();
    }
  }, [tagSet, toolType, numUsers, shownAttributes, additionalPipelineJobs, selectedUsers]);

  //Handlers
  const handleSliderChange = (event, newValue) => {
    setNumUsers(newValue);
  };

  const handleSelectAllGarments = (selectOrDeselectAll) => {
    const localRejectedTags = garments.map(garm => {return {
      garment_id: garm.garment_id,
      proposed_timestamp: getCurrentTimestamp(),
    }});
    
    setSelectedGarments(selectOrDeselectAll ? localRejectedTags : [])
    setSelectAllGarments(!selectAllGarments)
  }

  return (
    <Box
      sx={{
        p: 2,
        pt: 2
      }}
    >
      <LoadingOverlay open={loading} />

      <Typography variant="h2" sx={{ 
        mb: 1, 
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: "100%"
      }}>
        {formatToSpacedCapitalizedWords(toolName)}
      </Typography>

      <Grid container
        spacing={2} sx={{
          my: 1,
          display: "flex",
          justifyContent: "space-between !important",
          alignItems: "center",
        }}
      >
        <Grid item xs={12} md={6} lg={2} sx={{
          justifyContent: "center",
          alignItems: "center",
          mt: -4
          }}>
          <Typography id="tag-selector" variant="h8" gutterBottom>
            Selected Tag:
          </Typography>
          <Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            sx={{
              width: "100%",
              mt: 1,
            }}
          >
            <MenuItem key={null} value={null}>
              Select Tag
            </MenuItem>
            {tagSet.map((tag, tagIndex) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name} ({qaCountObjects[tag.id] || 0})
              </MenuItem>
            ))}
          </Select>
        </Grid>
        
        <Grid item xs={12} md={6} lg={2} sx={{
          justifyContent: "center",
          alignItems: "center",
          }}>
          <PipelineJobMultiSelect
              sx={{mt: -0.5}}
              selected={additionalPipelineJobs}
              onSelectedChange={setAdditionalPipelineJobs}
              label={"Select Pipeline Jobs"}
            />
        </Grid>

        <Grid item xs={12} md={12} lg={3} sx={{
          justifyContent: "center",
          alignItems: "center",
          }}>
          <Typography id="input-slider" variant="body" gutterBottom>
            Select a number of users:
          </Typography>
          <Slider
            value={numUsers}
            min={1}
            max={10}
            marks
            step={1}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            aria-labelledby="input-slider"
            sx={{mx: 1}}
          />
          <Typography variant="body">Selected Number: {numUsers}</Typography>
        </Grid>

        <Grid item xs={6} md={6} lg={1}>
          <Select
            value={numberOfColumns}
            onChange={(e) => setNumberOfColumns(e.target.value)}
            displayEmpty
          >
            <MenuItem value={4}>4 Columns</MenuItem>
            <MenuItem value={5}>5 Columns</MenuItem>
            <MenuItem value={6}>6 Columns</MenuItem>
            <MenuItem value={7}>7 Columns</MenuItem>
            <MenuItem value={8}>8 Columns</MenuItem>
          </Select>
        </Grid>
        
        <Grid item xs={12} md={6} lg={4}>
          <Grid container spacing={1}>
            {/* Top row: Sort Options and Filter Options */}
            <Grid item xs={6}>
              <StyledButton
                variant="outlined"
                color="primary"
                onClick={() => setIsSortModalOpen(true)}
                sx={{ width: "100%", mt: -0.5 }}
              >
                Sort Options
              </StyledButton>
            </Grid>
            <Grid item xs={6}>
              <StyledButton
                variant="outlined"
                color="primary"
                onClick={() => setIsFilterModalOpen(true)}
                sx={{ width: "100%", mt: -0.5 }}
              >
                Filter Options
              </StyledButton>
            </Grid>
            {/* Bottom row: Select All and Submit */}
            <Grid item xs={6}>
              <Button
                variant="contained-white"
                color="primary"
                sx={[{
                  width: "100%",
                  py: 1,
                  },
                  selectAllGarments && {
                      backgroundColor: `${palette.primary[300]} !important`,
                      color: `${palette.primary.main} !important`
                  }]}
                onClick={() => handleSelectAllGarments(!selectAllGarments)}
              >
                {selectAllGarments ? `DESELECT` : `SELECT`} ALL
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained-white"
                color="primary"
                sx={{
                  width: "100%",
                  py: 1,
                }}
                onClick={() => submitItems()}
              >
                SUBMIT
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Sort Options Modal */}
      <Dialog
        open={isSortModalOpen}
        onClose={() => setIsSortModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sort Options</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body_md" sx={{ mb: 2 }}>
                Sort Priority
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button
                  variant={sortBy === "confidence" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setSortBy("confidence")}
                  sx={{ flex: 1 }}
                >
                  Confidence First
                </Button>
                <Button
                  variant={sortBy === "submitted_timestamp" ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setSortBy("submitted_timestamp")}
                  sx={{ flex: 1 }}
                >
                  Submitted Date First
                </Button>
              </Box>
              
              <Typography variant="body_md" sx={{ mb: 2 }}>
                Confidence Sorting
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button
                  variant={!orderByConfidenceAsc ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setOrderByConfidenceAsc(false)}
                  sx={{ flex: 1 }}
                >
                  HIGH → LOW
                </Button>
                <Button
                  variant={orderByConfidenceAsc ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setOrderByConfidenceAsc(true)}
                  sx={{ flex: 1 }}
                >
                  LOW → HIGH
                </Button>
              </Box>
              
              <Typography variant="body_md" sx={{ mb: 2 }}>
                Submitted Date Sorting
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={!orderBySubmittedTimestampAsc ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setOrderBySubmittedTimestampAsc(false)}
                  sx={{ flex: 1 }}
                >
                  NEWEST → OLDEST
                </Button>
                <Button
                  variant={orderBySubmittedTimestampAsc ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setOrderBySubmittedTimestampAsc(true)}
                  sx={{ flex: 1 }}
                >
                  OLDEST → NEWEST
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
          <StyledButton
            variant="outlined"
            color="primary"
            onClick={() => setIsSortModalOpen(false)}
          >
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Filter Options Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Options</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body_md" sx={{ mb: 2 }}>
                Proposed By (Users)
              </Typography>
              <SearchableMultiSelectV2
                typeMapping={"USER"}
                selected={selectedUsers}
                onSelectedChange={setSelectedUsers}
                label={"Select Users"}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body_md" sx={{ mb: 2 }}>
                Additional Pipeline Jobs
              </Typography>
              <PipelineJobMultiSelect
                selected={additionalPipelineJobs}
                onSelectedChange={setAdditionalPipelineJobs}
                label={"Select Pipeline Jobs"}
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
          <StyledButton
            variant="outlined"
            color="primary"
            onClick={() => setIsFilterModalOpen(false)}
          >
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      {selectedTag ? (
        (garments.length === 0) & !loading ? (
          <NoDataScreenCover showBackButton={false} />
        ) : (
          <MassImageApprovalsFrame
            garments={garments}
            selectedGarments={selectedGarments}
            setSelectedGarments={setSelectedGarments}
            shownAttributes={shownAttributes}
            numberOfColumns={numberOfColumns}
            showTitle={true}
            showBrand={true}
          />
        )
      ) : (
        <TakeActionScreenCover />
      )}
    </Box>
  );
}

function WrappedGarmentQaTool() {
  //Job Pipeline ID
  const { id } = useParams();
  return (
    <ToolProvider id={id}>
      <GarmentAttributeQaTool />
    </ToolProvider>
  );
}

export default WrappedGarmentQaTool;
